/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type CSSProperties } from 'react';
import { jsx } from '@compiled/react';
import { cssMap } from '@atlaskit/css';
import { ModalBody } from '@atlaskit/modal-dialog';
import { token } from '@atlaskit/tokens';
import { isCustomDimensions, isNamedSize } from '../../custom-ui/bridge/types';
import type { ModalNamedSizes, ModalSizes } from '../../custom-ui/bridge/types';

type ModalContentWrapperProps = {
	children: JSX.Element;
	isJsmModule?: boolean;
	isResizable?: boolean;
	size: ModalNamedSizes;
	styles?: CSSProperties;
};

const sizeStyles = cssMap({
	small: {
		height: '20vh',
		minHeight: '320px',
		backgroundColor: token('elevation.surface.overlay'),
		borderRadius: token('radius.small', '3px'),
	},
	medium: {
		height: '40vh',
		minHeight: '520px',
		backgroundColor: token('elevation.surface.overlay'),
		borderRadius: token('radius.small', '3px'),
	},
	large: {
		height: '70vh',
		minHeight: '720px',
		backgroundColor: token('elevation.surface.overlay'),
		borderRadius: token('radius.small', '3px'),
	},
	xlarge: {
		// default behaviour and height from ModalBody is used here.
		// If a static height is used, it will not be responsive and overflow off the screen and viewport height is too small.
		height: '90vh',
		// @ts-expect-error – cssMap types don't support calc() expressions but this is valid CSS
		minHeight: 'calc(100vh - 119px)',
		backgroundColor: token('elevation.surface.overlay'),
		borderRadius: token('radius.small', '3px'),
	},
	max: {
		height: '100%',
		backgroundColor: token('elevation.surface.overlay'),
		borderRadius: token('radius.small', '3px'),
	},
	fullscreen: {
		height: '100%',
		backgroundColor: token('elevation.surface.overlay'),
	},
	resizable: {
		// Min dimensions are 'small', max dimensions are 'max' size (see platform/packages/forge/forge-ui/src/custom-ui/modal.ts)
		minWidth: '400px', // Source: platform/packages/design-system/modal-dialog/src/internal/width.tsx
		// @ts-expect-error – cssMap types don't support calc() expressions but this is valid CSS
		maxWidth: 'calc(100vw - 120px)', // Source: platform/packages/design-system/modal-dialog/src/internal/components/positioner.tsx
		minHeight: '320px',
		maxHeight: '100%',
		width: 'auto',
		height: 'auto',
		overflow: 'auto',
		display: 'flex',
		flexDirection: 'column',
		backgroundColor: token('elevation.surface.overlay'),
		borderRadius: token('radius.small', '3px'),
	},
});

const resizableStyles = cssMap({
	jsmModal: {
		overflow: 'auto',
		height: 'auto',
	},
	// Used for named-size modals in the legacy switch path where isResizable=true.
	// Matches the original resizableStyles.default before EXT-4856 expanded it.
	namedSize: {
		overflow: 'auto',
		height: 'auto',
	},
	default: {
		// Min dimensions are 'small', max dimensions are 'max' size (see platform/packages/forge/forge-ui/src/custom-ui/modal.ts)
		minWidth: '400px', // Source: platform/packages/design-system/modal-dialog/src/internal/width.tsx
		// @ts-expect-error – cssMap types don't support calc() expressions but this is valid CSS
		maxWidth: 'calc(100vw - 120px)', // Source: platform/packages/design-system/modal-dialog/src/internal/components/positioner.tsx
		minHeight: '320px',
		maxHeight: '100%',
		width: 'auto',
		height: 'auto',
		overflow: 'auto',
		display: 'flex',
		flexDirection: 'column',
		backgroundColor: token('elevation.surface.overlay'),
		borderRadius: token('radius.small', '3px'),
	},
});

const namedSizes = new Set(Object.keys(sizeStyles) as Array<keyof typeof sizeStyles>);
export const ModalContentWrapper = ({
	children,
	size,
	styles,
	isResizable,
	isJsmModule,
}: ModalContentWrapperProps): JSX.Element => {
	const namedSizeKey = namedSizes.has(size as string) ? (size as keyof typeof sizeStyles) : null;

	if (!namedSizeKey) {
		return <ModalBody>{children}</ModalBody>;
	}

	// Modals in JSM modules have only been resizable height-wise but not width wise.
	// We are unsure on the business context behind this, so maintain the existing styling in case we break something.
	// For JSM: apply resizableStyles to any non-max/non-fullscreen named size (existing behaviour).
	// For non-JSM: only apply resizableStyles when the size is explicitly 'resizable' — named sizes like
	// 'xlarge' must not have their fixed vh height overridden by resizableStyles (which sets height: auto).
	if (isJsmModule) {
		const supportsJsmResizable =
			isResizable && namedSizeKey !== 'max' && namedSizeKey !== 'fullscreen';
		return (
			<div
				css={[sizeStyles[namedSizeKey], supportsJsmResizable && resizableStyles.jsmModal]}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
				style={styles}
			>
				{children}
			</div>
		);
	}

	// For the 'resizable' size, apply the full resizable modal styles.
	// For other named sizes, apply only the minimal overflow/height styles (matching pre-EXT-4856 behaviour).
	const supportsResizable = isResizable && namedSizeKey !== 'max' && namedSizeKey !== 'fullscreen';
	const useFullResizableModalStyles = supportsResizable && namedSizeKey === 'resizable';

	if (useFullResizableModalStyles) {
		return (
			<div
				css={[sizeStyles[namedSizeKey], resizableStyles.default]}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
				style={styles}
			>
				{children}
			</div>
		);
	}

	return (
		<div
			css={[sizeStyles[namedSizeKey], supportsResizable && resizableStyles.namedSize]}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
			style={styles}
		>
			{children}
		</div>
	);
};

export const getAtlasKitModalWidth = (
	modalSize: ModalNamedSizes,
): 'small' | 'medium' | 'large' | 'x-large' | '100%' | 'auto' => {
	if (modalSize === 'xlarge') {
		return 'x-large';
	}
	if (modalSize === 'max' || modalSize === 'fullscreen') {
		return '100%';
	}

	if (modalSize === 'resizable') {
		return 'auto';
	}
	return modalSize;
};

export const getExtensionIcon = (extension: {
	properties?: { config?: { icon?: string }; icon?: string };
}): string | undefined => {
	return extension?.properties?.config?.icon ?? extension?.properties?.icon;
};

type ResolveModalDimensionsOptions = {
	getModalWidth?: (size: ModalNamedSizes | undefined) => string | undefined;
	isResizableSize: boolean;
	shouldFallbackFromFullscreen: boolean;
	size: ModalSizes | undefined;
};

type ModalDimensions = {
	/** Size to pass to ModalContentWrapper — 'max' for custom dimensions so the iframe fills the full modal */
	contentSize: ModalNamedSizes;
	/** Height to pass to the ADS <Modal> component */
	height: string | undefined;
	/** Width to pass to the ADS <Modal> component */
	width: string | undefined;
};

const getHeight = ({
	customDimensions,
	isResizableSize,
	namedSize,
	shouldFallbackFromFullscreen,
}: {
	customDimensions: { height?: string } | undefined;
	isResizableSize: boolean;
	namedSize: ModalNamedSizes;
	shouldFallbackFromFullscreen: boolean;
}): string | undefined => {
	if (customDimensions?.height) {
		return customDimensions.height;
	}
	if (isResizableSize) {
		return 'auto';
	}
	if (namedSize === 'max' || shouldFallbackFromFullscreen) {
		return '100%';
	}
	return undefined;
};

/**
 * Consolidates all modal dimension logic into one place.
 *
 * Priority for named size: modalWidthForMacroCustomConfig → size (string) → viewportSize → 'medium'
 * Priority for width: getModalWidth override → customDimensions.width → ADS named width
 * Priority for height: customDimensions.height → 'auto' (for isResizableSize) → '100%' (for max/fullscreen fallback) → undefined (ModalContentWrapper handles it)
 */
export const resolveModalDimensions = ({
	size,
	getModalWidth,
	shouldFallbackFromFullscreen,
	isResizableSize,
}: ResolveModalDimensionsOptions): ModalDimensions => {
	const hasCustomDimensions = !!size && isCustomDimensions(size);
	const customDimensions = hasCustomDimensions ? size : undefined;
	const namedSize: ModalNamedSizes = size !== undefined && isNamedSize(size) ? size : 'medium';

	const namedWidth = getModalWidth ? getModalWidth(namedSize) : getAtlasKitModalWidth(namedSize);
	const resolvedWidth = customDimensions?.width ?? namedWidth;

	const width = shouldFallbackFromFullscreen ? '100%' : resolvedWidth;

	const height = getHeight({
		customDimensions,
		isResizableSize,
		namedSize,
		shouldFallbackFromFullscreen,
	});

	// When custom dimensions are provided, the <Modal> component is sized via explicit width/height props.
	// ModalContentWrapper must use 'max' (height: 100%) so the iframe fills the full modal height
	// rather than being constrained to a named size's fixed vh value (e.g. 'medium' = 40vh).
	const contentSize = customDimensions ? 'max' : namedSize;

	return { width, height, contentSize };
};
